import type { HebrewLevel, LearningGoal } from "@/types";

export const LEVEL_LABELS: Record<HebrewLevel, { label: string; description: string; emoji: string }> = {
  beginner: { label: "Just Starting", description: "I'm brand new to Hebrew", emoji: "🌱" },
  basics: { label: "Know Some Basics", description: "I know the alphabet and a few words", emoji: "📖" },
  conversational: { label: "Conversational", description: "I can hold a basic conversation", emoji: "💬" },
  advanced: { label: "Advanced", description: "I'm comfortable but want more vocabulary", emoji: "🎯" },
};

export const GOAL_LABELS: Record<LearningGoal, { label: string; description: string; emoji: string }> = {
  aliyah: { label: "Moving to Israel", description: "Preparing for life in Israel", emoji: "✈️" },
  heritage: { label: "Heritage & Family", description: "Connecting with my roots", emoji: "👨‍👩‍👧‍👦" },
  religious: { label: "Religious Study", description: "Torah, prayer & Jewish learning", emoji: "📜" },
  travel: { label: "Travel", description: "Visiting Israel or Hebrew-speaking areas", emoji: "🗺️" },
  military: { label: "Joining the IDF", description: "Preparing for army service", emoji: "🪖" },
  general: { label: "General Interest", description: "Learning for fun or personal growth", emoji: "⭐" },
};

/** Starter prompts that pre-fill the text area when a goal chip is tapped */
export const GOAL_PROMPTS: Record<LearningGoal, string> = {
  aliyah: "I'm preparing to move to Israel. I need vocabulary for daily life — grocery shopping, public transport, talking to landlords, and navigating government offices.",
  heritage: "I want to connect with my Jewish heritage and communicate with Hebrew-speaking family. I'm interested in family, holidays, traditions, and cultural expressions.",
  religious: "I'm studying Torah and Jewish texts. I need vocabulary for prayer, liturgy, blessings, and religious concepts.",
  travel: "I'm planning a trip to Israel. I need practical vocabulary for airports, restaurants, hotels, asking directions, and shopping.",
  military: "I'm preparing for IDF service. I need vocabulary for following commands, military life, equipment, ranks, and everyday conversation on base.",
  general: "I'm learning Hebrew for personal growth. I want a well-rounded starter vocabulary covering greetings, food, numbers, common verbs, and everyday descriptions.",
};

export interface ChipOption {
  slug: string;
  label: string;
  emoji: string;
}

export const DAILY_ACTIVITIES: ChipOption[] = [
  { slug: "workplace", label: "Workplace & Office", emoji: "💼" },
  { slug: "shopping", label: "Shopping & Errands", emoji: "🛒" },
  { slug: "restaurants", label: "Restaurants & Cafes", emoji: "☕" },
  { slug: "getting-around", label: "Getting Around", emoji: "🚌" },
  { slug: "healthcare", label: "Healthcare", emoji: "🏥" },
  { slug: "parenting", label: "Parenting & Kids", emoji: "👶" },
  { slug: "school", label: "School & University", emoji: "🎓" },
  { slug: "social", label: "Meeting People", emoji: "👋" },
  { slug: "bureaucracy", label: "Banks & Government", emoji: "🏛️" },
  { slug: "home", label: "Home & Apartment", emoji: "🏠" },
];

export const INTERESTS: ChipOption[] = [
  { slug: "music", label: "Music", emoji: "🎵" },
  { slug: "film-tv", label: "Film & TV", emoji: "🎬" },
  { slug: "sports", label: "Sports", emoji: "⚽" },
  { slug: "politics", label: "Politics", emoji: "🗳️" },
  { slug: "food-culture", label: "Food Culture", emoji: "🥘" },
  { slug: "nature", label: "Nature & Hiking", emoji: "🌿" },
  { slug: "technology", label: "Technology", emoji: "📱" },
  { slug: "art", label: "Art & Design", emoji: "🎨" },
  { slug: "history", label: "History", emoji: "📚" },
  { slug: "religion", label: "Religion & Spirituality", emoji: "✡️" },
  { slug: "business", label: "Business", emoji: "📊" },
  { slug: "travel", label: "Travel", emoji: "✈️" },
];

const LEVEL_PROMPT_MODIFIERS: Record<HebrewLevel, string> = {
  beginner: "Very basic, high-frequency words a complete beginner would need. Simple nouns and common verbs in infinitive form.",
  basics: "Elementary vocabulary for someone who knows the Hebrew alphabet and basic words.",
  conversational: "Intermediate vocabulary to expand conversational ability. Include some less common but practical words.",
  advanced: "Advanced, nuanced vocabulary including idiomatic expressions and less common words.",
};

export function getLevelPromptModifier(level: HebrewLevel): string {
  return LEVEL_PROMPT_MODIFIERS[level];
}
